import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Lightbulb, TrendingUp, TriangleAlert } from 'lucide-react';
import type { Brief, CitedPoint, DocumentDetail } from '../api/schemas';
import { useBriefQuery } from '../hooks/useDocumentQuery';
import { PointSection } from './PointSection';
import { ReportForecasts } from './ReportForecasts';

type Props = { doc: DocumentDetail; onOpen: (page: number, passage: string) => void };

const firstSentence = (text: string) => /^.*?[.!?](?=\s|$)/.exec(text)?.[0] ?? text;

/** The checked calls when there is a brief; otherwise the report's own first-page bullets. */
const callsOf = (brief: Brief | undefined, doc: DocumentDetail): CitedPoint[] => (brief && brief.calls.length > 0 ? brief.calls : doc.keyPoints);

/** The report in one line, then its calls, drivers and risks, each opening the page it comes from. The calls, drivers
 * and risks are written once from this report and checked against the cited page. Forecasts come straight from the
 * report's table. */
export function SummaryTab({ doc, onOpen }: Props) {
  const brief = useBriefQuery(doc.id);
  const calls = callsOf(brief.data, doc);
  const oneLine = doc.keyPoints[0]?.text ?? calls[0]?.text;

  return (
    <Stack sx={{ gap: 2.5, p: 2 }}>
      {oneLine ? (
        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'surface.subtle' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>In one line</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>{firstSentence(oneLine)}</Typography>
        </Box>
      ) : null}
      {brief.isPending ? <Writing /> : <Sections calls={calls} brief={brief.data} onOpen={onOpen} />}
      <ReportForecasts forecasts={doc.forecasts} onOpenPage={(p) => { onOpen(p, ''); }} />
    </Stack>
  );
}

function Writing() {
  return (
    <Stack sx={{ gap: 1 }} aria-busy="true">
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Summarising this report, with a page for every point…</Typography>
      {['100%', '92%', '84%'].map((w) => <Skeleton key={w} variant="rounded" width={w} height={36} />)}
    </Stack>
  );
}

type SectionsProps = { calls: CitedPoint[]; brief: Brief | undefined; onOpen: Props['onOpen'] };

function Sections({ calls, brief, onOpen }: SectionsProps) {
  if (calls.length === 0) {
    return <Typography variant="body2" sx={{ color: 'text.secondary' }}>This report has no summary points on its first page. The full report is on the left.</Typography>;
  }
  return (
    <>
      <PointSection title="Key calls" icon={Lightbulb} points={calls} onOpen={onOpen} />
      <PointSection title="Drivers" icon={TrendingUp} points={brief?.drivers ?? []} onOpen={onOpen} />
      <PointSection title="Risks" icon={TriangleAlert} points={brief?.risks ?? []} onOpen={onOpen} />
    </>
  );
}
